import { RuleProps } from 'types/application';

export const SECT_COMPANY_DETAILS = 'company-details';
export const SECT_FILE_ATTACHMENTS = 'file-attachments';
export const SECT_EVENT_DETAILS = 'event-details';
export const SECT_EVENT_VENUE = 'event-venue';
export const SECT_PERFORMER = 'performer';
export const SECT_DOCS = 'supporting-docs';
export const SECT_OWNERSHIP = 'ownership';
export const SECT_FINANCIAL_DETAILS = 'financial-details';
export const SECT_FUNDING_CREDIT_INFORMATION = 'funding-credit-information';
export const SECT_BUSINESS_PLANS = 'business-plans';
export const SECT_PERSONNEL = 'personnel';
export const SECT_POLICIES_OPERATIONS = 'policies-and-operations';
export const SECT_INFORMATION_INSOLVENCY = 'information-insolvency';

interface UploadedFile {
    deleted?: boolean;
    name?: string;
    url?: string;
}

function validateUploadedFiles(uploadedFilesRaw: unknown, errorMessage = 'Field is required'): true | string {
    // Narrow to array safely
    const uploadedFiles: UploadedFile[] = Array.isArray(uploadedFilesRaw) ? uploadedFilesRaw : [];
    const validFiles = uploadedFiles.filter(file => !file.deleted);

    return validFiles.length === 0 ? errorMessage : true;
}


export const ACA_APPLICATION_FIELDS = [
    {
        sectionID: SECT_COMPANY_DETAILS,
        sectionName: 'Company Details',
        fieldList: [
            {
                id: 'registered-as',
                label: 'Registered as',
                type: 'dropdown',
                apiID: 'CACompanyDetails',
                options: [
                    { optID: 'Public Limited (limited by shares)', optLabel: 'Public Limited (limited by shares)' },
                    { optID: 'Private Limited (non-exempt limited by shares)', optLabel: 'Private Limited (non-exempt limited by shares)' },
                    { optID: 'Others', optLabel: 'Others' }
                ],
                rule: {
                    required: true,
                }
            },
            {
                id: 'registered-as-others',
                label: 'Please specify',
                type: 'text',
                placeholder: 'Please specify',
                rule: {
                    baseIds: [`${SECT_COMPANY_DETAILS}-registered-as`],
                    required: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_COMPANY_DETAILS}-registered-as`] === 'CAOT');
                        return result;
                    },
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_COMPANY_DETAILS}-registered-as`] !== 'CAOT');
                        return result;
                    }
                }
            },
            {
                id: 'principal-business-activities',
                label: 'Principal Business Activities',
                type: 'longText',
                span: 12,
                textRows: 4,
                rule: { required: true }
            },
            {
                id: 'company-website-url',
                label: 'Company Website URL',
                placeholder: 'Eg: https://yourwebsite.com/',
                type: 'url',
                span: 12,
                rule: {
                    required: true
                },
                labelNote: 'The URL provided should refer to the web site that contains information on the Certification Authority and its Certification Practice Statement(s).'
            },
            {
                id: 'company-documents',
                label: 'Company Documents',
                type: 'helperList',
                span: 12,
                rule: {
                    baseIds: [`${SECT_COMPANY_DETAILS}-company-documents`],
                    required: true,
                    customValidate: ({ baseValues }: RuleProps) => {
                        const uploadedFilesRaw = baseValues?.[`${SECT_COMPANY_DETAILS}-company-documents-0-company-documents-upload`];
                        return validateUploadedFiles(uploadedFilesRaw, 'Company documents are required');
                    },
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`transaction-type`] !== 'Draft');
                        return result;
                    }
                },
                customListExpect: [],
                mainField: {
                    id: 'company-documents-upload',
                    label: 'Upload Company Documents',
                    type: 'file',
                    category: 'CompanyDocuments',
                    rule: {
                        required: true,
                        errorMsg: 'Field is required'
                    },
                    fileTypes: ['.pdf', '.xls', '.xlsx', '.doc', '.docx'],
                    mimeTypes: [
                        'application/pdf',
                        'application/vnd.ms-excel',
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        'application/msword',
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                    ]
                },
                customList: [
                    {
                        expect: [], // Always show this list
                        title: 'Please attach the following:',
                        list: [
                            'A recent original Business Profile Report on the applicant company obtained from the Accounting and Corporate Regulatory Authority of Singapore.',
                            {
                                text: 'A certified true copy of the applicant company\'s resolution(s):',
                                listType: 'decimal',
                                subItems: [
                                    { text: 'to apply an accredited Certification Authority ("CA"), or (in the case of an accredited CA applying for renewal of its accreditation) for renewal of its accreditation; and' },
                                    { text: 'to authorise, for the purpose of making this application on the applicant company\'s behalf, the person(s) making the application.' }
                                ]
                            }
                        ]
                    }
                ]
            }
        ]
    },
    {
        sectionID: SECT_OWNERSHIP,
        sectionName: 'Ownership',
        note: 'Please provide complete shareholding information for the applicant company.',
        fieldList: [
            {
                id: 'shareholder-list',
                label: 'Shareholders',
                type: 'table',
                subFields: [
                    {
                        id: 'name',
                        header: 'Name of Company/Individual',
                        type: 'text',
                        rule: {
                            required: true,
                        }
                    },
                    {
                        id: 'country',
                        header: 'Country of Incorporation',
                        type: 'dropdown',
                        apiID: 'Country',
                        rule: {
                            required: true
                        }
                    },
                    {
                        id: 'share-percentage',
                        header: '% Share',
                        type: 'number',
                        step: 0.01,
                        rule: {
                            required: true,
                            min: 0,
                            max: 100
                        }
                    },
                    {
                        id: 'nature-of-business',
                        header: 'Nature of Business',
                        type: 'text',
                        rule: {
                            required: true
                        }
                    },
                ],
                span: 12,
                buttonTitle: 'Add Shareholder',
                minForm: 1,
                defaultRows: 1,
                rule: {
                    required: true,
                    minLength: 1,
                    baseIds: [`${SECT_OWNERSHIP}-shareholder-list`],
                    customValidate: ({ currentValue }: RuleProps) => {
                        // Use currentValue (the array itself) instead of baseValues['shareholder-list']
                        const shareholderListRaw = (currentValue || []) as Array<Record<string, any>>;
                        
                        if (!shareholderListRaw || !Array.isArray(shareholderListRaw) || shareholderListRaw.length === 0) {
                            return true; // Let required validation handle empty cases
                        }

                        // Type definition for shareholder object
                        interface Shareholder {
                            deleted?: boolean;
                            ['share-percentage']: string | number;
                            [key: string]: any;
                        }

                        // Filter out deleted shareholders
                        const shareholderList = shareholderListRaw.filter((s) => !s?.deleted);

                        // Calculate total percentage
                        const total = (shareholderList as Shareholder[]).reduce((sum: number, shareholder) => {
                            const percentage = parseFloat(String(shareholder['share-percentage'])) || 0;
                            return sum + percentage;
                        }, 0);

                        // Round to 2 decimal places to handle floating point precision issues
                        const roundedTotal = Math.round(total * 100) / 100;

                        if (roundedTotal !== 100) {
                            return `Total share percentage must equal 100%. Currently: ${roundedTotal}%`;
                        }

                        return true;
                    }
                }
            },
            {
                id: 'shareholder-documents',
                label: 'Shareholding Information Documents',
                type: 'helperList',
                span: 12,
                rule: {
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`transaction-type`] !== 'Draft');
                        return result;
                    }
                },
                customListExpect: [],
                mainField: {
                    id: 'shareholder-documents-upload',
                    label: '',
                    type: 'file',
                    category: 'ShareholderDocuments',
                    rule: {},
                    fileTypes: ['.pdf', '.xls', '.xlsx', '.doc', '.docx'],
                    mimeTypes: [
                        'application/pdf',
                        'application/vnd.ms-excel',
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        'application/msword',
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                    ]
                },
                customList: [
                    {
                        expect: [],
                        title: 'Where the applicant company is a subsidiary of another company, please upload information about the parent and ultimate holding companies (the entire group structure) below.',
                        list: []
                    }
                ]
            }
        ]
    },
    {
        sectionID: SECT_FINANCIAL_DETAILS,
        sectionName: 'Financial Statements For The Past 3 Years',
        fieldList: [
            {
                id: 'statement-list',
                label: '',
                type: 'table',
                subFields: [
                    {
                        id: 'year',
                        header: 'Year Ending',
                        type: 'number',
                        rule: {
                            required: true,
                            customValidate: ({ currentValue }: RuleProps) => {
                                const currentYear = new Date().getFullYear();
                                const threeYearsAgo = currentYear - 3;
                                const year = parseInt(currentValue as string, 10);
                                
                                if (isNaN(year)) {
                                    return 'Please enter a valid year';
                                }
                                if (year > currentYear) {
                                    return 'Year cannot be in the future';
                                }
                                if (year < threeYearsAgo) {
                                    return `Year must be within the past 3 years (${threeYearsAgo} - ${currentYear})`;
                                }
                                return true;
                            }
                        }
                    },
                    {
                        id: 'revenue',
                        header: 'Revenue (S$)',
                        type: 'text',
                        formatNumber: true,
                        rule: {
                            required: true
                        }
                    },
                    {
                        id: 'profits-before-tax',
                        header: 'Profits before tax (S$)',
                        type: 'text',
                        formatNumber: true,
                        rule: {
                            required: true
                        }
                    },
                    {
                        id: 'profits-after-tax',
                        header: 'Profits after tax (S$)',
                        type: 'text',
                        formatNumber: true,
                        rule: {
                            required: true
                        }
                    },
                    {
                        id: 'total-assets',
                        header: 'Total assets (S$)',
                        type: 'text',
                        formatNumber: true,
                        rule: {
                            required: true
                        }
                    },
                    {
                        id: 'total-liabilities',
                        header: 'Total liabilities (S$)',
                        type: 'text',
                        formatNumber: true,
                        rule: {
                            required: true
                        }
                    },
                ],
                span: 12,
                minForm: 3,
                defaultRows: 3,
                staticRows: true,
                rule: {
                    required: true,
                    minLength: 3
                }
            },
            {
                id: 'auditors-list',
                label: 'Auditors',
                type: 'table',
                subFields: [
                    {
                        id: 'name',
                        header: 'Name of Auditor',
                        label: 'Name of Auditor',
                        type: 'text',
                        rule: {
                            required: true,
                        }
                    },
                    {
                        id: 'postal',
                        header: 'Postal Code',
                        label: 'Postal Code',
                        apiID: 'PostalCode',
                        type: 'postal',
                        rule: { required: true }
                    },
                    {
                        id: 'building',
                        header: 'Building Name',
                        label: 'Building Name',
                        apiID: 'BuildingName',
                        type: 'text',
                        rule: { charLength: 66 }
                    },
                    {
                        id: 'block-house',
                        header: 'Block/House No.',
                        label: 'Block/House No.',
                        apiID: 'BlockNumber',
                        type: 'dropdown',
                        rule: { 
                            required: true, 
                            charLength: 10
                        }
                    },
                    {
                        id: 'street',
                        header: 'Street Name',
                        label: 'Street Name',
                        apiID: 'StreetName',
                        type: 'text',
                        rule: {
                            required: true,
                            charLength: 32
                        }
                    },
                    {
                        id: 'floor',
                        header: 'Floor No.',
                        label: 'Floor No.',
                        apiID: 'FloorNumber',
                        type: 'text',
                        placeholder: 'Eg: 01, B1, 01M',
                        rule: { 
                            charLength: 3
                        }
                    },
                    {
                        id: 'unit',
                        header: 'Unit No.',
                        label: 'Unit No.',
                        apiID: 'UnitNumber',
                        type: 'text',
                        placeholder: 'Eg: #05-01, use 01',
                        rule: { 
                            charLength: 5
                        }
                    },
                    {
                        id: 'address',
                        header: 'Address',
                        type: 'virtual' // Virtual field for display only
                    }
                ],
                span: 12,
                buttonTitle: 'Add Auditor',
                modalForm: true, // This enables modal form instead of inline
                displayFields: ['name', 'address'], // Show name and formatted address in table
                minForm: 1, // Minimum 1 auditor required - will show 1 initial row
                rule: {
                    required: true,
                    minLength: 1 // At least 1 auditor must be provided
                }
            },
            {
                id: 'auditor-documents',
                label: '',
                type: 'helperList',
                span: 12,
                rule: {
                    baseIds: [`${SECT_FINANCIAL_DETAILS}-auditor-documents`],
                    required: true,
                    customValidate: ({ baseValues }: RuleProps) => {
                        const uploadedFilesRaw = baseValues?.[`${SECT_FINANCIAL_DETAILS}-auditor-documents-0-auditor-documents-upload`];
                        return validateUploadedFiles(uploadedFilesRaw, 'Auditor documents are required');
                    },
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`transaction-type`] !== 'Draft');
                        return result;
                    }
                },
                customListExpect: [],
                mainField: {
                    id: 'auditor-documents-upload',
                    label: '',
                    type: 'file',
                    category: 'AuditorDocuments',
                    rule: {
                        required: true,
                        errorMsg: 'Field is required'
                    },
                    fileTypes: ['.pdf', '.xls', '.xlsx', '.doc', '.docx'],
                    mimeTypes: [
                        'application/pdf',
                        'application/vnd.ms-excel',
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        'application/msword',
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                    ]
                },
                customList: [
                    {
                        expect: [],
                        title: 'Please provide the audited accounts of the applicant company for the past 3 years. Where the applicant company is a subsidiary of another company, the audited accounts of the parent and ultimate holding companies must be provided below.',
                        list: []
                    }
                ]
            }
        ]
    },
    {
        sectionID: SECT_FUNDING_CREDIT_INFORMATION,
        sectionName: 'Funding & Credit information',
        fieldList: [
            {
                id: 'funding-credit-information-list',
                label: '',
                type: 'table',
                subFields: [
                    {
                        id: 'source-of-funding',
                        header: 'Source of Funding',
                        type: 'text',
                        rule: {
                            required: true,
                        }
                    },
                    {
                        id: 'amount',
                        header: 'Amount (S$)',
                        type: 'number',
                        step: 0.01,
                        formatNumber: true,
                        rule: {
                            required: true,
                            min: 0
                        }
                    },
                    {
                        id: 'details-of-any-collateral',
                        header: 'Details of any collateral',
                        type: 'text',
                        rule: {
                            required: true
                        }
                    },
                    {
                        id: 'name-of-contact-person-for-verification',
                        header: 'Name of contact person for verification',
                        type: 'text',
                        rule: {
                            required: true
                        }
                    },
                    {
                        id: 'contact-number',
                        header: 'Contact number',
                        type: 'number',
                        rule: {
                            required: true
                        }
                    },
                    {
                        id: 'email-address',
                        header: 'Email address',
                        type: 'email',
                        rule: {
                            required: true
                        }
                    },
                ],
                span: 12,
                minForm: 1,
                defaultRows: 1,
                buttonTitle: 'Add Funding & Credit Info',
                rule: {
                    required: true,
                    minLength: 1
                }
            },
        ]
    },
    {
        sectionID: SECT_BUSINESS_PLANS,
        sectionName: 'Business Plans',
        rule: {
            baseIds: [`${SECT_BUSINESS_PLANS}-business-plans-documents`],
            hidden: ({ baseValues }: RuleProps) => {
                const result = Boolean(baseValues && baseValues[`transaction-type`] !== 'Draft');
                return result;
            }
        },
        fieldList: [
            {
                id: 'business-plans-documents',
                label: '',
                type: 'helperList',
                span: 12,
                rule: {
                    baseIds: [`${SECT_BUSINESS_PLANS}-business-plans-documents`],
                    required: true,
                    customValidate: ({ baseValues }: RuleProps) => {
                        const uploadedFilesRaw = baseValues?.[`${SECT_BUSINESS_PLANS}-business-plans-documents-0-business-plans-documents-upload`];
                        return validateUploadedFiles(uploadedFilesRaw, 'Business plans documents are required');
                    },
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`transaction-type`] !== 'Draft');
                        return result;
                    }
                },
                customListExpect: [],
                mainField: {
                    id: 'business-plans-documents-upload',
                    label: 'Please explain any discrepancies between the figures.',
                    type: 'file',
                    category: 'BusinessPlansDocuments',
                    rule: {
                        required: true,
                        errorMsg: 'Field is required'
                    },
                    fileTypes: ['.pdf', '.xls', '.xlsx', '.doc', '.docx'],
                    mimeTypes: [
                        'application/pdf',
                        'application/vnd.ms-excel',
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        'application/msword',
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                    ]
                },
                customList: [
                    {
                        expect: [],
                        title: 'Please provide a copy of the applicant company\'s business plans for the CA operations for the current and next 3 financial years. This should include the following:',
                        list: [
                            'Business statement',
                            'Target market',
                            'Business strategy',
                            'Projected monthly cash flow statements',
                            'Budgets for the current and next 3 financial years'
                        ]
                    }
                ]
            }
        ]
    },
    {
        sectionID: SECT_POLICIES_OPERATIONS,
        sectionName: 'Policies And Operations',
        rule: {
            baseIds: [`${SECT_POLICIES_OPERATIONS}-policies-and-operations-documents`],
            hidden: ({ baseValues }: RuleProps) => {
                const result = Boolean(baseValues && baseValues[`transaction-type`] !== 'Draft');
                return result;
            }
        },
        fieldList: [
            {
                id: 'policies-and-operations-documents',
                label: '',
                type: 'helperList',
                span: 12,
                rule: {
                    baseIds: [`${SECT_POLICIES_OPERATIONS}-policies-and-operations-documents`],
                    required: true,
                    customValidate: ({ baseValues }: RuleProps) => {
                        const uploadedFilesRaw = baseValues?.[`${SECT_POLICIES_OPERATIONS}-policies-and-operations-documents-0-policies-and-operations-documents-upload`];
                        return validateUploadedFiles(uploadedFilesRaw, 'Policies and operations documents are required');
                    },
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`transaction-type`] !== 'Draft');
                        return result;
                    }
                },
                customListExpect: [],
                mainField: {
                    id: 'policies-and-operations-documents-upload',
                    label: '',
                    type: 'file',
                    category: 'PoliciesOperationsDocuments',
                    rule: {
                        required: true,
                        errorMsg: 'Field is required'
                    },
                    fileTypes: ['.pdf', '.xls', '.xlsx', '.doc', '.docx'],
                    mimeTypes: [
                        'application/pdf',
                        'application/vnd.ms-excel',
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        'application/msword',
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                    ]
                },
                customList: [
                    {
                        expect: [],
                        title: 'Please provide a copy of the following:',
                        listType: 'lower-alpha',
                        list: [
                            'Certification Practice Statement(s) of the CA ("CPS");',
                            'Technical specifications of the CA\'s system;',
                            'CA\'s public key certificate (to provide a certified hardcopy and a softcopy);',
                            'CA\'s security policies and standards;',
                            'CA\'s incident management plan;',
                            'CA\'s business continuity plan;',
                            'Audit report prepared in accordance with the Regulations for compliance with the Compliance Audit Checklist published on the Controller\'s Internet website; and',
                            'Credentials of the auditing team conducting audit and preparing the audit report (referred to in (g) above), demonstrating compliance with the auditors\' qualifications as specified in the Regulations.',
                        ],
                        note: 'Some of the information can be provided later if it is not available at time of application.'
                    }
                ]
            }
        ]
    },
    {
        sectionID: SECT_PERSONNEL,
        sectionName: 'Personnel',
        rule: {
            baseIds: [`${SECT_PERSONNEL}-personnel-documents`],
            hidden: ({ baseValues }: RuleProps) => {
                const result = Boolean(baseValues && baseValues[`transaction-type`] !== 'Draft');
                return result;
            }
        },
        fieldList: [
            {
                id: 'personnel-documents',
                label: '',
                type: 'helperList',
                span: 12,
                rule: {
                    baseIds: [`${SECT_PERSONNEL}-personnel-documents`],
                    required: true,
                    customValidate: ({ baseValues }: RuleProps) => {
                        const uploadedFilesRaw = baseValues?.[`${SECT_PERSONNEL}-personnel-documents-0-personnel-documents-upload`];
                        return validateUploadedFiles(uploadedFilesRaw, 'Personnel documents are required');
                    },
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`transaction-type`] !== 'Draft');
                        return result;
                    }
                },
                customListExpect: [],
                mainField: {
                    id: 'personnel-documents-upload',
                    label: '',
                    type: 'file',
                    category: 'PersonnelDocuments',
                    rule: {
                        required: true,
                        errorMsg: 'Field is required'
                    },
                    fileTypes: ['.pdf', '.xls', '.xlsx', '.doc', '.docx'],
                    mimeTypes: [
                        'application/pdf',
                        'application/vnd.ms-excel',
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        'application/msword',
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                    ]
                },
                customList: [
                    {
                        expect: [],
                        title: 'Please provide:',
                        listType: 'lower-alpha',
                        list: [
                            {
                                text: 'The following information for each and every trusted person (as defined in the Regulations):',
                                listType: 'disc',
                                subItems: [
                                    { text: 'Name' },
                                    { text: 'NRIC Number/Passport Number/Foreign Identification Number' },
                                    { text: 'Residential Address' },
                                    { text: 'Designation and Function' },
                                    { text: 'Date of Employment' },
                                    { text: 'Details of qualifications and experience to carry out his/her duties as a trusted person' }
                                ]
                            },
                            'A copy of the applicant company\'s organisation chart.'
                        ]
                    }
                ]
            }
        ]
    },
    {
        sectionID: SECT_INFORMATION_INSOLVENCY,
        sectionName: 'Information on Insolvency & Legal Proceedings',
        fieldList: [
            {
                id: 'involved-legal',
                label: 'Has the applicant company ever been involved in any legal proceeding or dispute settlement here or anywhere else in the world in its capacity as a Certification Authority?',
                type: 'radio',
                options: [
                    {
                        optID: 'Yes',
                        optLabel: 'Yes'
                    },
                    {
                        optID: 'No',
                        optLabel: 'No'
                    }
                ],
                rule: {
                    required: true
                },
                span: 12
            },
            {
                id: 'involved-legal-details',
                label: 'Please furnish complete details',
                type: 'longText',
                span: 12,
                rule: {
                    baseIds: [`${SECT_INFORMATION_INSOLVENCY}-involved-legal`],
                    required: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_INFORMATION_INSOLVENCY}-involved-legal`] === 'Yes');
                        return result;
                    },
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_INFORMATION_INSOLVENCY}-involved-legal`] !== 'Yes');
                        return result;
                    }

                }
            },
            {
                id: 'substantial-shareholder',
                label: 'Is the applicant company or its substantial shareholder in the course of being wound up or liquidated?',
                type: 'radio',
                options: [
                    {
                        optID: 'Yes',
                        optLabel: 'Yes'
                    },
                    {
                        optID: 'No',
                        optLabel: 'No'
                    }
                ],
                rule: {
                    baseIds: [`${SECT_INFORMATION_INSOLVENCY}-substantial-shareholder`],
                    required: true
                },
                span: 12
            },
            {
                id: 'substantial-shareholder-details',
                label: 'Please furnish complete details',
                type: 'longText',
                span: 12,
                rule: {
                    baseIds: [`${SECT_INFORMATION_INSOLVENCY}-substantial-shareholder`],
                    required: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_INFORMATION_INSOLVENCY}-substantial-shareholder`] === 'Yes');
                        return result;
                    },
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_INFORMATION_INSOLVENCY}-substantial-shareholder`] !== 'Yes');
                        return result;
                    }

                }
            },
            {
                id: 'receiver-manager-shareholder',
                label: 'Has a receiver, or a receiver and manager, been appointed to the applicant company or its substantial shareholder?',
                type: 'radio',
                options: [
                    {
                        optID: 'Yes',
                        optLabel: 'Yes'
                    },
                    {
                        optID: 'No',
                        optLabel: 'No'
                    }
                ],
                rule: {
                    required: true
                },
                span: 12
            },
            {
                id: 'receiver-manager-shareholder-details',
                label: 'Please furnish complete details',
                type: 'longText',
                span: 12,
                rule: {
                    baseIds: [`${SECT_INFORMATION_INSOLVENCY}-receiver-manager-shareholder`],
                    required: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_INFORMATION_INSOLVENCY}-receiver-manager-shareholder`] === 'Yes');
                        return result;
                    },
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_INFORMATION_INSOLVENCY}-receiver-manager-shareholder`] !== 'Yes');
                        return result;
                    }

                }
            },
            {
                id: 'compromise-creditors',
                label: 'Has the applicant company or its substantial shareholder, whether in Singapore or elsewhere, entered into any compromise or scheme of arrangement with its creditors?',
                type: 'radio',
                options: [
                    {
                        optID: 'Yes',
                        optLabel: 'Yes'
                    },
                    {
                        optID: 'No',
                        optLabel: 'No'
                    }
                ],
                rule: {
                    required: true
                },
                span: 12
            },
            {
                id: 'compromise-creditors-details',
                label: 'Please furnish complete details, including whether the compromise or scheme of arrangement is still in operation',
                type: 'longText',
                span: 12,
                rule: {
                    baseIds: [`${SECT_INFORMATION_INSOLVENCY}-compromise-creditors`],
                    required: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_INFORMATION_INSOLVENCY}-compromise-creditors`] === 'Yes');
                        return result;
                    },
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_INFORMATION_INSOLVENCY}-compromise-creditors`] !== 'Yes');
                        return result;
                    }

                }
            },
            {
                id: 'fraudulent-dishonest',
                label: 'Has the applicant company or its substantial shareholder, any of their respective directors and key executives, or any of the trusted persons, ever been convicted of an offence for which the conviction involved a finding that it/he/she acted fraudulently or dishonestly?',
                type: 'radio',
                options: [
                    {
                        optID: 'Yes',
                        optLabel: 'Yes'
                    },
                    {
                        optID: 'No',
                        optLabel: 'No'
                    }
                ],
                rule: {
                    required: true
                },
                span: 12
            },
            {
                id: 'fraudulent-dishonest-details',
                label: 'Please furnish complete details',
                type: 'longText',
                span: 12,
                rule: {
                    baseIds: [`${SECT_INFORMATION_INSOLVENCY}-fraudulent-dishonest`],
                    required: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_INFORMATION_INSOLVENCY}-fraudulent-dishonest`] === 'Yes');
                        return result;
                    },
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_INFORMATION_INSOLVENCY}-fraudulent-dishonest`] !== 'Yes');
                        return result;
                    }

                }
            }
        ]
    }
];